import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { TrendingUp, Eye, MousePointerClick, FileText, Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useProjection } from '@/hooks/useBalance';
import { Card, CardContent, ErrorState } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import { formatReais } from '@/lib/utils';

// Sem centavos: aqui o número é ordem de grandeza, não valor exato.
const formatReaisSemCentavos = (value: number) => formatReais(value, { hideCents: true });

function formatNumber(value: number): string {
  return value.toLocaleString('pt-BR');
}

const SLIDER_MIN = 1000;
const SLIDER_MAX = 50000;
const SLIDER_STEP = 500;

export function InvestmentProjectionPage() {
  const [amount, setAmount] = useState(5000);
  const { data, isLoading, error, refetch } = useProjection(amount);

  return (
    <motion.div
      className="space-y-8"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="text-center">
        <h1 className="text-3xl font-bold">Simule seu investimento</h1>
        <p className="mt-2 text-muted-foreground">
          Veja o que seu investimento pode gerar em impressões, cliques e orçamentos influenciados
        </p>
      </div>

      {/* Slider */}
      <div className="mx-auto max-w-md space-y-3">
        <div className="text-center text-4xl font-bold text-primary">
          {formatReaisSemCentavos(amount)}
        </div>
        <input
          type="range"
          min={SLIDER_MIN}
          max={SLIDER_MAX}
          step={SLIDER_STEP}
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
          className="w-full accent-primary"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{formatReaisSemCentavos(SLIDER_MIN)}</span>
          <span>{formatReaisSemCentavos(SLIDER_MAX)}</span>
        </div>
      </div>

      {/* Data source badge */}
      {data && (
        <div className="text-center">
          <Badge variant={data.basedOnHistoricalData ? 'success' : 'secondary'}>
            {data.basedOnHistoricalData
              ? 'Baseado em dados reais da plataforma'
              : 'Estimativa com médias do mercado'}
          </Badge>
        </div>
      )}

      {/* Tier cards */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : data ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {data.tiers.map((tier, i) => {
            const isMain = i === 0;
            return (
              <motion.div
                key={tier.amount}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className={isMain ? 'border-primary ring-2 ring-primary/20' : ''}>
                  <CardContent className="space-y-4 pt-6">
                    <div className="text-center">
                      {isMain && (
                        <Badge variant="success" className="mb-2">
                          <Sparkles size={12} className="mr-1" />
                          Selecionado
                        </Badge>
                      )}
                      <div className="text-2xl font-bold">{formatReaisSemCentavos(tier.amount)}</div>
                      <div className="text-xs text-muted-foreground">
                        ~{tier.daysOfCoverage} dias de cobertura
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Eye size={14} />
                          Impressões
                        </div>
                        <span className="text-sm font-bold">{formatNumber(tier.estimatedImpressions)}</span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MousePointerClick size={14} />
                          Cliques
                        </div>
                        <span className="text-sm font-bold">{formatNumber(tier.estimatedClicks)}</span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <FileText size={14} />
                          Orçamentos
                        </div>
                        <span className="text-sm font-bold">{formatNumber(tier.estimatedQuotes)}</span>
                      </div>

                      <div className="flex items-center justify-between border-t border-border pt-3">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <TrendingUp size={14} />
                          ROI estimado
                        </div>
                        <span className="text-lg font-bold text-primary">
                          {tier.estimatedRoi !== null ? `${tier.estimatedRoi}x` : '—'}
                        </span>
                      </div>
                    </div>

                    <Link
                      to="/balance/top-up"
                      className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                    >
                      Investir {formatReaisSemCentavos(tier.amount)}
                      <ArrowRight size={14} />
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      ) : (
        /* Era `null`: a grade sumia e o rodape sobre "valores estimados"
           continuava ali, pendurado em cima do nada — sugerindo que os numeros
           vieram e eram esses. Nada indicava que a busca falhou. */
        <ErrorState
          title="Não foi possível calcular a projeção"
          error={error}
          onRetry={() => void refetch()}
        />
      )}

      {/* Disclaimer — so faz sentido junto de valores que existem. */}
      {data && (
        <p className="text-center text-xs text-muted-foreground">
          * Valores estimados com base em dados históricos. Resultados reais podem variar conforme categoria, região e sazonalidade.
        </p>
      )}
    </motion.div>
  );
}
